import React, { useState, useCallback, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { View, StyleSheet, Dimensions, Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import ReactNativeZoomableView from '@openspacelabs/react-native-zoomable-view/src/ReactNativeZoomableView';
import InfiniteGrid from './InfiniteGrid';
import { ZoomableGridViewProps, ZoomTransformEvent, DEFAULT_ZOOM_CONFIG, DEFAULT_GRID_CONFIG } from './types';

/**
 * Ref handle for imperative control of the zoomable view
 * Only available when not using external zoomableViewRef
 */
export interface ZoomableGridViewRef {
  /** Zoom to a specific level */
  zoomTo: (zoom: number) => void;
  /** Move to a specific position */
  moveTo: (x: number, y: number) => void;
  /** Zoom by a relative amount */
  zoomBy: (zoomAmount: number) => void;
  /** Get current zoom state */
  getZoomState: () => { zoomLevel: number; offsetX: number; offsetY: number };
}

/**
 * ZoomableGridView - Reusable component combining infinite grid with zoom/pan capabilities
 * 
 * Two modes of operation:
 * 
 * 1. **Standalone Mode** (default): Creates its own ReactNativeZoomableView wrapper
 *    @example
 *    ```tsx
 *    <ZoomableGridView cellSize={50} initialZoom={1}>
 *      <YourContent />
 *    </ZoomableGridView>
 *    ```
 * 
 * 2. **Grid-Only Mode**: Reads from existing zoomable view via props
 *    @example
 *    ```tsx
 *    const zoomRef = useRef();
 *    const [zoomState, setZoomState] = useState({ zoomLevel: 1, offsetX: 0, offsetY: 0 });
 * 
 *    <ReactNativeZoomableView 
 *      ref={zoomRef} 
 *      onTransform={(e) => setZoomState({ 
 *        zoomLevel: e.zoomLevel, 
 *        offsetX: -e.offsetX, 
 *        offsetY: -e.offsetY 
 *      })}
 *    >
 *      <ZoomableGridView 
 *        zoomableViewRef={zoomRef}
 *        externalZoomLevel={zoomState.zoomLevel}
 *        externalOffsetX={zoomState.offsetX}
 *        externalOffsetY={zoomState.offsetY}
 *        cellSize={50}
 *      >
 *        <YourContent />
 *      </ZoomableGridView>
 *    </ReactNativeZoomableView>
 *    ```
 */
const ZoomableGridView = forwardRef<ZoomableGridViewRef, ZoomableGridViewProps>(
  (
    {
      children,
      zoomableViewRef: externalZoomRef,
      externalZoomLevel,
      externalOffsetX,
      externalOffsetY,
      initialZoom = DEFAULT_ZOOM_CONFIG.initialZoom,
      minZoom = DEFAULT_ZOOM_CONFIG.minZoom,
      maxZoom = DEFAULT_ZOOM_CONFIG.maxZoom,
      zoomStep = DEFAULT_ZOOM_CONFIG.zoomStep,
      contentWidth = DEFAULT_ZOOM_CONFIG.contentWidth,
      contentHeight = DEFAULT_ZOOM_CONFIG.contentHeight,
      bindToBorders = DEFAULT_ZOOM_CONFIG.bindToBorders,
      showGrid = DEFAULT_ZOOM_CONFIG.showGrid,
      initialOffsetX,
      initialOffsetY,
      onZoomChange,
      style,
      cellSize = DEFAULT_GRID_CONFIG.cellSize,
      dotMode = DEFAULT_GRID_CONFIG.dotMode,
      dotRadius = DEFAULT_GRID_CONFIG.dotRadius,
      lineWidth = DEFAULT_GRID_CONFIG.lineWidth,
      color = DEFAULT_GRID_CONFIG.color,
      opacity = DEFAULT_GRID_CONFIG.opacity,
    },
    ref
  ) => {
    // Determine mode: standalone or grid-only
    const isGridOnlyMode = !!externalZoomRef;

    // Get viewport dimensions
    const { width: viewportWidth, height: viewportHeight } = Dimensions.get('window');

    // Ref to access zoomable view methods (standalone mode)
    const internalZoomRef = useRef<any>(null);
    const activeZoomRef = externalZoomRef || internalZoomRef;

    // Performance optimization: Use state for values that affect rendering (standalone mode)
    const [internalZoomLevel, setInternalZoomLevel] = useState(initialZoom);
    const [internalOffsetX, setInternalOffsetX] = useState(initialOffsetX || 0);
    const [internalOffsetY, setInternalOffsetY] = useState(initialOffsetY || 0);

    // Use external values in grid-only mode, internal values in standalone mode
    const zoomLevel = isGridOnlyMode ? (externalZoomLevel ?? initialZoom) : internalZoomLevel;
    const offsetX = isGridOnlyMode ? (externalOffsetX ?? 0) : internalOffsetX;
    const offsetY = isGridOnlyMode ? (externalOffsetY ?? 0) : internalOffsetY;

    // Performance optimization: Use ref to track last update time for throttling
    const lastUpdateRef = useRef<number>(0);
    const UPDATE_THRESHOLD_MS = 16; // ~60fps

    // Performance optimization: Throttled transform handler (for standalone mode)
    const handleTransform = useCallback(
      (event: ZoomTransformEvent) => {
        if (isGridOnlyMode) return; // Don't handle transforms in grid-only mode

        const now = Date.now();
        
        // Throttle updates to avoid excessive re-renders
        if (now - lastUpdateRef.current >= UPDATE_THRESHOLD_MS) {
          lastUpdateRef.current = now;
          
          // Update state for grid rendering
          setInternalZoomLevel(event.zoomLevel);
          setInternalOffsetX(-event.offsetX);
          setInternalOffsetY(-event.offsetY);

          // Notify parent component
          onZoomChange?.(event.zoomLevel, -event.offsetX, -event.offsetY);
        }
      },
      [isGridOnlyMode, onZoomChange]
    );

    // Expose imperative methods via ref (only in standalone mode)
    useImperativeHandle(
      ref,
      () => ({
        zoomTo: (zoom: number) => {
          if (!isGridOnlyMode) {
            activeZoomRef.current?.zoomTo(zoom);
          }
        },
        moveTo: (x: number, y: number) => {
          if (!isGridOnlyMode) {
            activeZoomRef.current?.moveTo(x, y);
          }
        },
        zoomBy: (zoomAmount: number) => {
          if (!isGridOnlyMode) {
            activeZoomRef.current?.zoomBy(zoomAmount);
          }
        },
        getZoomState: () => ({
          zoomLevel,
          offsetX,
          offsetY,
        }),
      }),
      [isGridOnlyMode, zoomLevel, offsetX, offsetY]
    );

    // Render grid component
    const gridComponent = showGrid && (
      <InfiniteGrid
        zoomLevel={zoomLevel}
        offsetX={offsetX}
        offsetY={offsetY}
        viewportWidth={viewportWidth}
        viewportHeight={viewportHeight}
        cellSize={cellSize}
        dotMode={dotMode}
        dotRadius={dotRadius}
        lineWidth={lineWidth}
        color={color}
        opacity={opacity}
      />
    );

    // Grid-only mode: Just render grid and children
    if (isGridOnlyMode) {
      return (
        <View style={[styles.gridOnlyContainer, style]}>
          {gridComponent}
          {children}
        </View>
      );
    }

    // Standalone mode: Render with own zoomable wrapper
    return (
      <GestureHandlerRootView style={[styles.container, style]}>
        <ReactNativeZoomableView
          ref={internalZoomRef}
          maxZoom={maxZoom}
          minZoom={minZoom}
          initialZoom={initialZoom}
          zoomStep={zoomStep}
          contentWidth={contentWidth}
          contentHeight={contentHeight}
          bindToBorders={bindToBorders}
          initialOffsetX={initialOffsetX}
          initialOffsetY={initialOffsetY}
          onTransform={handleTransform}
          style={styles.zoomableView}
        >
          {gridComponent}
          <View style={styles.contentContainer}>
            {children}
          </View>
        </ReactNativeZoomableView>
      </GestureHandlerRootView>
    );
  }
);

ZoomableGridView.displayName = 'ZoomableGridView';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  zoomableView: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
  },
  gridOnlyContainer: {
    ...StyleSheet.absoluteFillObject,
    flex: 1,
  },
});

export default ZoomableGridView;

